import React from 'react';
import { Box, Typography, Card, CardContent, Button, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useSpring, animated } from 'react-spring';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

const brandColor = '#0B60B1';
const brandGradient = 'linear-gradient(135deg, #0B60B1 0%, #1e90ff 100%)';

const products = [
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />, 
    title: 'IQDMARC',
    desc: 'Proactive email authentication management. Prevent phishing, stop spoofing, and ensure every message you send is trusted.',
    cta: 'Request a Demo',
    link: '/products/iqdmarc',
    color: brandColor,
    gradient: brandGradient,
    features: ['DMARC Monitoring', 'SPF & DKIM', 'Email Authentication', 'Phishing Protection'],
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />, 
    title: 'SPAM0',
    desc: 'Enterprise-grade SaaS for domain & IP reputation. Monitor blacklists, diagnose spam issues, and keep your emails in the inbox.',
    cta: 'See Details',
    link: '/products/spam0',
    color: brandColor,
    gradient: brandGradient,
    features: ['Blacklist Monitoring', 'IP Reputation', 'Spam Score', 'Deliverability Analytics'],
  },
];

const ProductsSection: React.FC = () => {
  const containerAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    config: { tension: 280, friction: 20 }
  });

  return (
    <Box sx={{ py: { xs: 6, md: 12 }, background: '#f7fafd', position: 'relative', overflow: 'hidden' }}>
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `radial-gradient(${brandColor} 1px, transparent 1px)`,
          backgroundSize: '30px 30px',
          pointerEvents: 'none',
        }}
      />

      <animated.div style={containerAnimation}>
        <Typography 
          variant="h3" 
          fontWeight={800} 
          align="center" 
          mb={2}
          sx={{ 
            background: brandGradient,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Our Products
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" mb={6} sx={{ maxWidth: 600, mx: 'auto' }}>
          Enterprise-grade solutions to protect and optimize your email infrastructure
        </Typography>
      </animated.div>

      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
          },
          gap: 4,
          maxWidth: 1200,
          mx: 'auto',
          px: 2,
        }}
      >
        {products.map((product, idx) => (
          <motion.div
            key={product.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
            whileHover={{ y: -5 }}
          >
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'visible',
                borderRadius: 4,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: product.gradient,
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                }}
              />
              <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 3, color: brandColor }}>
                  {product.icon}
                </Box>
                <Typography variant="h4" fontWeight={700} mb={2} color={brandColor}>
                  {product.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={3} sx={{ flexGrow: 1 }}>
                  {product.desc}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {product.features.map((feature) => (
                    <Chip
                      key={feature}
                      label={feature}
                      size="small"
                      sx={{
                        mr: 1,
                        mb: 1,
                        backgroundColor: 'rgba(11,96,177,0.08)',
                        color: brandColor,
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
                <Button 
                  component={Link}
                  to={product.link}
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    alignSelf: 'flex-start',
                    color: brandColor,
                    borderColor: brandColor,
                    '&:hover': {
                      borderColor: brandColor,
                      backgroundColor: 'rgba(11,96,177,0.04)',
                    },
                  }}
                >
                  {product.cta}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

export default ProductsSection; 